// Command palette controller - opened with Cmd+K / Ctrl+K.
// Displays a searchable list of keyboard shortcuts and knowledge base articles.
// Users can filter by typing, navigate with arrow keys, and execute with Enter.
//
// Shortcut items dispatch to the shortcut registry.
// Help article items open in a new browser tab.

import { Controller } from "@hotwired/stimulus"
import lunr from "lunr"
import showConfetti from "../utils/confetti"

const KB_CACHE_TTL_MS = 5 * 60 * 1000

export default class extends Controller {
  static targets = ["dialog", "input", "list", "empty"]
  static values = {
    knowledgeBaseUrls: { type: Array, default: [] },
    settingsItems: { type: Array, default: [] },
  }

  connect() {
    this.handleGlobalKeydown = this.handleGlobalKeydown.bind(this)
    document.addEventListener("keydown", this.handleGlobalKeydown)
    this.selectedIndex = -1
    this.kbArticles = null
    this.kbIndex = null
    this.kbFetchedAt = null
    this.buildSettingsIndex()
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleGlobalKeydown)
  }

  handleGlobalKeydown(event) {
    if ((event.metaKey || event.ctrlKey) && event.key === "k") {
      event.preventDefault()
      this.toggle()
      return
    }

    if (event.key === "?" && !this.isInputFocused(event)) {
      event.preventDefault()
      this.open()
    }
  }

  toggle() {
    if (this.dialogTarget.open) {
      this.close()
    } else {
      this.open()
    }
  }

  open() {
    this.loadShortcuts()
    this.fetchKbArticles()
    this.dialogTarget.showModal()
    this.inputTarget.value = ""
    this.inputTarget.focus()
    this.selectedIndex = -1
    this.applyFilter()
  }

  close() {
    this.dialogTarget.close()
    this.inputTarget.value = ""
  }

  backdropClose(event) {
    if (event.target === this.dialogTarget) {
      this.close()
    }
  }

  handleDialogKeydown(event) {
    if (event.key === "Escape") {
      event.preventDefault()
      this.close()
      return
    }

    if (event.key === "ArrowDown") {
      event.preventDefault()
      this.moveSelection(1)
      return
    }

    if (event.key === "ArrowUp") {
      event.preventDefault()
      this.moveSelection(-1)
      return
    }

    if (event.key === "Enter") {
      event.preventDefault()
      this.executeSelected()
    }
  }

  filter() {
    clearTimeout(this.filterTimeout)
    this.filterTimeout = setTimeout(() => this.applyFilter(), 150)
  }

  applyFilter() {
    const query = this.inputTarget.value.toLowerCase().trim()

    // Filter shortcuts and easter eggs with fuzzy lunr search
    const matchedIds = query && this.shortcutIndex ? this.fuzzySearch(this.shortcutIndex, query) : null
    const staticItems = this.listTarget.querySelectorAll("[data-command-palette-item]:not([data-kb-result]):not([data-settings-result])")
    let visibleCount = 0

    staticItems.forEach((item) => {
      const id = item.dataset.shortcutKey || item.dataset.actionType || ""
      const match = !query || (matchedIds !== null && matchedIds.has(id))
      item.classList.toggle("hidden", !match)
      if (match) visibleCount++
    })

    // Show/hide category headers for static items
    this.listTarget.querySelectorAll("[data-category-header]:not([data-category-header='Help Articles'])").forEach((header) => {
      const category = header.dataset.categoryHeader
      const hasVisible = this.listTarget.querySelector(
        `[data-command-palette-item][data-item-category="${category}"]:not(.hidden):not([data-kb-result])`
      )
      header.classList.toggle("hidden", !hasVisible)
    })

    // Remove previous dynamic results (settings + KB)
    this.listTarget
      .querySelectorAll("[data-settings-result], [data-category-header='Settings'], [data-kb-result], [data-category-header='Help Articles']")
      .forEach((el) => el.remove())

    // Search settings (actionable in-app navigation) — ranked above help articles
    if (query && this.settingsIndex) {
      const settingsResults = this.searchSettings(query)
      if (settingsResults.length > 0) {
        visibleCount += settingsResults.length
        this.renderSettingsResults(settingsResults)
      }
    }

    // Search KB with lunr when there's a query
    if (query && this.kbIndex) {
      const kbResults = this.searchKb(query)
      if (kbResults.length > 0) {
        visibleCount += kbResults.length
        this.renderKbResults(kbResults)
      }
    }

    this.emptyTarget.classList.toggle("hidden", visibleCount > 0)
    this.selectedIndex = visibleCount > 0 ? 0 : -1
    this.updateSelection()
  }

  // Shared fuzzy search: exact (boost 10), stemmed prefix (boost 5), fuzzy edit distance 1.
  // Lunr skips the stemmer for wildcard queries, so we stem manually before adding the
  // trailing wildcard. This lets "apps" → stem "app" → "app*" match "applic(ation)".
  fuzzySearch(index, query) {
    const terms = query.split(/\s+/).filter(Boolean)
    try {
      return new Set(
        index.query((q) => {
          terms.forEach((term) => {
            q.term(term, { boost: 10 })
            const stemmed = lunr.stemmer(new lunr.Token(term)).toString()
            q.term(stemmed, { usePipeline: false, wildcard: lunr.Query.wildcard.TRAILING, boost: 5 })
            q.term(term, { editDistance: 1 })
          })
        }).map((r) => r.ref)
      )
    } catch {
      return new Set()
    }
  }

  searchKb(query) {
    const terms = query.split(/\s+/).filter(Boolean)
    let results
    try {
      results = this.kbIndex.query((q) => {
        terms.forEach((term) => {
          q.term(term, { boost: 10 })
          const stemmed = lunr.stemmer(new lunr.Token(term)).toString()
          q.term(stemmed, { usePipeline: false, wildcard: lunr.Query.wildcard.TRAILING, boost: 5 })
          q.term(term, { editDistance: 1 })
        })
      })
    } catch {
      return []
    }

    // Deduplicate by base URL, keeping highest-scored result
    const seen = new Map()
    results.forEach((result) => {
      const article = this.kbArticlesMap.get(result.ref)
      if (!article) return
      const baseUrl = article.url.split("#")[0]
      if (!seen.has(baseUrl) || result.score > seen.get(baseUrl).score) {
        seen.set(baseUrl, result)
      }
    })

    return Array.from(seen.values()).slice(0, 10).map((result) => {
      const article = this.kbArticlesMap.get(result.ref)
      const snippet = this.extractSnippet(article, result.matchData.metadata)
      return { article, score: result.score, snippet }
    })
  }

  extractSnippet(article, metadata) {
    for (const term of Object.keys(metadata)) {
      const fields = metadata[term]

      // Prefer content for snippets since title is already visible
      if (fields.content && fields.content.position) {
        const [start] = fields.content.position[0]
        return this.snippetAround(article.content, start, term.length)
      }

      if (fields.section && article.section) {
        return article.section
      }
    }
    return null
  }

  snippetAround(text, matchStart, matchLen) {
    const contextChars = 40
    const start = Math.max(0, matchStart - contextChars)
    const end = Math.min(text.length, matchStart + matchLen + contextChars)
    let snippet = text.substring(start, end).trim()
    if (start > 0) snippet = `…${snippet}`
    if (end < text.length) snippet = `${snippet}…`
    return snippet
  }

  moveSelection(direction) {
    const visible = this.visibleItems()
    if (visible.length === 0) return

    this.selectedIndex = (this.selectedIndex + direction + visible.length) % visible.length
    this.updateSelection()
  }

  executeSelected() {
    const visible = this.visibleItems()
    if (this.selectedIndex < 0 || this.selectedIndex >= visible.length) return

    this.executeItemElement(visible[this.selectedIndex])
  }

  executeItem(event) {
    this.executeItemElement(event.currentTarget)
  }

  executeItemElement(item) {
    // Easter egg action
    if (item.dataset.actionType === "confetti") {
      this.close()
      showConfetti()
      return
    }

    // KB article - open in new tab
    if (item.dataset.kbUrl) {
      this.close()
      window.open(item.dataset.kbUrl, "_blank", "noopener")
      return
    }

    // Settings item - navigate within the app
    if (item.dataset.href) {
      this.close()
      if (window.Turbo) {
        window.Turbo.visit(item.dataset.href)
      } else {
        window.location.assign(item.dataset.href)
      }
      return
    }

    // Shortcut - dispatch to registry
    const key = item.dataset.shortcutKey
    if (!key) return

    this.close()
    document.body.dispatchEvent(
      new CustomEvent("shortcut:execute", { detail: { key } })
    )
  }

  // Private - data loading

  loadShortcuts() {
    const event = new CustomEvent("shortcut:list", {
      detail: { callback: (shortcuts) => { this.shortcuts = shortcuts } },
    })
    document.body.dispatchEvent(event)
    this.buildShortcutIndex()
    this.renderAll()
  }

  buildShortcutIndex() {
    const items = (this.shortcuts || []).map((s) => ({
      id: s.key,
      text: `${s.description} ${s.category} ${s.key}`,
    }))

    // Add easter eggs
    items.push({ id: "confetti", text: "confetti party celebration fun" })

    this.shortcutIndex = lunr(function () {
      this.ref("id")
      this.field("text")

      items.forEach((item) => this.add(item))
    })
  }

  // Settings items come from the server (CommunityAdminSite::Settings::Navigation,
  // via the settings-items value). They're static, so the index is built once on
  // connect and surfaced only when the user types a query (like KB articles).
  buildSettingsIndex() {
    const items = this.settingsItemsValue || []
    if (items.length === 0) {
      this.settingsIndex = null
      return
    }

    this.settingsItemsMap = new Map()
    items.forEach((item, i) => {
      const id = `setting-${i}`
      this.settingsItemsMap.set(id, { ...item, id })
    })

    const map = this.settingsItemsMap
    this.settingsIndex = lunr(function () {
      this.ref("id")
      this.field("text", { boost: 10 })
      this.field("keywords", { boost: 6 })
      this.field("group", { boost: 3 })

      map.forEach((item) => {
        this.add({ id: item.id, text: item.text, keywords: item.keywords || "", group: item.group || "" })
      })
    })
  }

  searchSettings(query) {
    if (!this.settingsIndex) return []

    return [...this.fuzzySearch(this.settingsIndex, query)]
      .slice(0, 8)
      .map((ref) => this.settingsItemsMap.get(ref))
      .filter(Boolean)
  }

  fetchKbArticles() {
    if (this.knowledgeBaseUrlsValue.length === 0) return
    if (this.kbArticles && this.kbFetchedAt && (Date.now() - this.kbFetchedAt < KB_CACHE_TTL_MS)) {
      return
    }

    this.fetchKbFromUrls([...this.knowledgeBaseUrlsValue])
  }

  fetchKbFromUrls(urls) {
    if (urls.length === 0) return

    // Intentionally a bare cross-origin fetch (kb.stokedhq.com), NOT
    // @rails/request.js: there's no CSRF to handle on a cross-origin GET, and
    // request.js always adds an `X-Requested-With` header, which would turn
    // this into a CORS-preflighted request the static KB host won't answer.
    const [url, ...remaining] = urls
    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error(`KB fetch failed: ${response.status}`)
        return response.json()
      })
      .then((data) => {
        this.activeKbBaseUrl = url
        this.kbArticles = this.parseKbData(data)
        this.kbFetchedAt = Date.now()
        this.renderAll()
        this.applyFilter()
      })
      .catch(() => {
        if (remaining.length > 0) {
          this.fetchKbFromUrls(remaining)
        }
        // Silently fail if no URLs remain - shortcuts still work without KB articles
      })
  }

  parseKbData(data) {
    const articles = new Map()

    Object.values(data).forEach((entry, i) => {
      const url = entry.url || entry.relUrl
      if (!url) return

      const title = entry.doc || entry.title || ""
      if (!title) return

      const id = `kb-${i}`
      articles.set(id, {
        id,
        title,
        url,
        content: (entry.content || "").substring(0, 300),
        section: entry.doc !== entry.title ? entry.doc : null,
        searchTerms: entry.search_terms || "",
      })
    })

    this.kbArticlesMap = articles
    this.kbIndex = lunr(function () {
      this.ref("id")
      this.field("title", { boost: 10 })
      this.field("section", { boost: 5 })
      this.field("searchTerms", { boost: 8 })
      this.field("content")
      this.metadataWhitelist = ["position"]

      articles.forEach((article) => {
        this.add({
          id: article.id,
          title: article.title,
          section: article.section || "",
          searchTerms: article.searchTerms,
          content: article.content,
        })
      })
    })

    return Array.from(articles.values())
  }

  // Private - rendering

  renderAll() {
    this.listTarget.innerHTML = ""
    this.renderShortcuts(this.shortcuts || [])
    this.renderEasterEggs()
  }

  renderShortcuts(shortcuts) {
    const grouped = {}
    shortcuts.forEach((shortcut) => {
      const cat = shortcut.category || "General"
      if (!grouped[cat]) grouped[cat] = []
      grouped[cat].push(shortcut)
    })

    const categoryOrder = ["Navigation", "Inbox", "General"]
    const sortedCategories = Object.keys(grouped).sort((a, b) => {
      const ai = categoryOrder.indexOf(a)
      const bi = categoryOrder.indexOf(b)
      if (ai === -1 && bi === -1) return a.localeCompare(b)
      if (ai === -1) return 1
      if (bi === -1) return -1
      return ai - bi
    })

    sortedCategories.forEach((category) => {
      this.renderCategoryHeader(category)

      grouped[category].forEach((shortcut) => {
        const item = document.createElement("button")
        item.type = "button"
        item.className = this.itemClasses()
        item.dataset.commandPaletteItem = ""
        item.dataset.itemCategory = category
        item.dataset.searchText = `${shortcut.description} ${shortcut.category} ${shortcut.key}`
        item.dataset.shortcutKey = shortcut.key
        item.dataset.action = "click->command-palette#executeItem"

        const descSpan = document.createElement("span")
        descSpan.textContent = shortcut.description

        const kbdContainer = document.createElement("span")
        kbdContainer.className = "flex gap-1 shrink-0"
        this.renderKeyBadges(shortcut.key, kbdContainer)

        item.appendChild(descSpan)
        item.appendChild(kbdContainer)
        this.listTarget.appendChild(item)
      })
    })
  }

  renderKbResults(results) {
    const category = "Help Articles"
    this.renderCategoryHeader(category)

    results.forEach(({ article, snippet }) => {
      const item = document.createElement("button")
      item.type = "button"
      item.className = this.itemClasses()
      item.dataset.commandPaletteItem = ""
      item.dataset.kbResult = ""
      item.dataset.itemCategory = category
      item.dataset.kbUrl = this.kbBaseUrl() + article.url
      item.dataset.action = "click->command-palette#executeItem"

      const left = document.createElement("span")
      left.className = "flex flex-col items-start min-w-0"

      const titleSpan = document.createElement("span")
      titleSpan.className = "truncate max-w-full"
      titleSpan.textContent = article.title
      left.appendChild(titleSpan)

      if (snippet) {
        const snippetSpan = document.createElement("span")
        snippetSpan.className = "text-xs text-gray-400 dark:text-neutral-500 truncate max-w-full"
        snippetSpan.textContent = snippet
        left.appendChild(snippetSpan)
      }

      const right = document.createElement("span")
      right.className = "flex items-center gap-1 shrink-0 text-xs text-gray-400 dark:text-neutral-500"
      right.innerHTML = this.externalLinkIcon()

      item.appendChild(left)
      item.appendChild(right)
      this.listTarget.appendChild(item)
    })
  }

  renderSettingsResults(results) {
    const category = "Settings"
    this.renderCategoryHeader(category)

    results.forEach((item) => {
      const el = document.createElement("button")
      el.type = "button"
      el.className = this.itemClasses()
      el.dataset.commandPaletteItem = ""
      el.dataset.settingsResult = ""
      el.dataset.itemCategory = category
      el.dataset.href = item.href
      el.dataset.action = "click->command-palette#executeItem"

      const left = document.createElement("span")
      left.className = "flex flex-col items-start min-w-0"

      const titleSpan = document.createElement("span")
      titleSpan.className = "truncate max-w-full"
      titleSpan.textContent = item.text
      left.appendChild(titleSpan)

      const groupSpan = document.createElement("span")
      groupSpan.className = "text-xs text-gray-400 dark:text-neutral-500 truncate max-w-full"
      groupSpan.textContent = item.group
      left.appendChild(groupSpan)

      el.appendChild(left)
      this.listTarget.appendChild(el)
    })
  }

  renderEasterEggs() {
    const category = "Fun"
    this.renderCategoryHeader(category)

    const item = document.createElement("button")
    item.type = "button"
    item.className = this.itemClasses()
    item.dataset.commandPaletteItem = ""
    item.dataset.itemCategory = category
    item.dataset.searchText = "confetti party celebration fun"
    item.dataset.actionType = "confetti"
    item.dataset.action = "click->command-palette#executeItem"

    const descSpan = document.createElement("span")
    descSpan.textContent = "Confetti!"

    item.appendChild(descSpan)
    this.listTarget.appendChild(item)
  }

  renderCategoryHeader(category) {
    const header = document.createElement("div")
    header.className =
      "px-3 py-2 text-xs font-semibold text-gray-500 dark:text-neutral-400 uppercase tracking-wider"
    header.textContent = category
    header.dataset.categoryHeader = category
    this.listTarget.appendChild(header)
  }

  renderKeyBadges(keyStr, container) {
    const keys = keyStr.split(" ")
    keys.forEach((key, i) => {
      if (i > 0) {
        const then = document.createElement("span")
        then.className = "text-gray-400 dark:text-neutral-500 text-xs self-center"
        then.textContent = "then"
        container.appendChild(then)
      }

      const badge = document.createElement("kbd")
      badge.className =
        "inline-flex items-center justify-center min-w-[1.5rem] px-1.5 py-0.5 " +
        "text-xs font-mono font-medium rounded-sm border " +
        "bg-gray-100 text-gray-600 border-gray-300 " +
        "dark:bg-neutral-700 dark:text-neutral-300 dark:border-neutral-600"
      badge.textContent = this.formatKeyLabel(key)
      container.appendChild(badge)
    })
  }

  formatKeyLabel(key) {
    const labels = {
      ctrl: "Ctrl",
      alt: "Alt",
      shift: "Shift",
      Enter: "Enter",
      Escape: "Esc",
      ArrowUp: "Up",
      ArrowDown: "Down",
      ArrowLeft: "Left",
      ArrowRight: "Right",
      " ": "Space",
    }
    return labels[key] || key.toUpperCase()
  }

  updateSelection() {
    const items = this.visibleItems()
    items.forEach((item, i) => {
      if (i === this.selectedIndex) {
        item.classList.add("bg-gray-100", "dark:bg-neutral-700")
        item.scrollIntoView({ block: "nearest" })
      } else {
        item.classList.remove("bg-gray-100", "dark:bg-neutral-700")
      }
    })
  }

  visibleItems() {
    return Array.from(
      this.listTarget.querySelectorAll("[data-command-palette-item]:not(.hidden)")
    )
  }

  isInputFocused(event) {
    const tag = event.target.tagName
    return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || event.target.isContentEditable
  }

  itemClasses() {
    return (
      "w-full flex items-center justify-between gap-3 px-3 py-2 text-sm rounded-lg " +
      "text-gray-700 dark:text-neutral-300 hover:bg-gray-100 dark:hover:bg-neutral-700 " +
      "focus:outline-hidden cursor-pointer"
    )
  }

  kbBaseUrl() {
    const sourceUrl = this.activeKbBaseUrl
    if (!sourceUrl) return ""
    try {
      const url = new URL(sourceUrl)
      return `${url.protocol}//${url.host}`
    } catch {
      return ""
    }
  }

  externalLinkIcon() {
    return '<svg class="w-3.5 h-3.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" /></svg>'
  }
}
